/**
 * cart-freebie.js
 * ─────────────────────────────────────────────────────────────────
 * Freebie selector — popup, add/change/remove freebie, threshold
 * validation, silent background validation on cart updates, and a
 * checkout guard that blocks checkout while a freebie is in the cart
 * below the eligibility threshold.
 * Depends on: cart-shared.js  (isWidgetEnabled, plumGetSharedCart,
 *                               plumBustSharedCart, plumSetSharedCart,
 *                               plumRefreshCart, window._plumOwnRefresh)
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  if (!isWidgetEnabled('freebie_selector')) return;

  var isValidating            = false;
  var isApplyingFreebie       = false;
  var isFreebieIneligible     = false; // true = freebie(s) in cart but threshold not met
  var validationTimer         = null;
  var popupCloseTimer         = null;
  var activeButton            = null;
  var activeButtonText        = '';
  var selectedFreebieVariantId = null;
  var pendingFreebieOperation = null; // tracks the in-flight add/change/validate promise

  var CART_ADD_URL    = window.Shopify.routes.root + 'cart/add.js';
  var CART_UPDATE_URL = window.Shopify.routes.root + 'cart/update.js';
  var CART_JS_URL     = window.Shopify.routes.root + 'cart.js';
  var SECTION_ID      = 'mini-cart';

  // Adjust this selector if the drawer/cart-page checkout button markup differs
  var CHECKOUT_SELECTOR = '.gokwik-checkout button, .gokwik-checkout .button--primary';
  var CHECKOUT_GUARD_TIMEOUT_MS = 4000; // fail-open after this, so we never trap a customer

  // ─── Config ───────────────────────────────────────────────────────────────

  function getConfig() {
    return {
      freebieIds: Array.isArray(window.__freebieIds)
        ? window.__freebieIds.map(Number)
        : [],
      threshold: Number(window.__freebieThreshold || 0)
    };
  }

  // ─── Button state helpers ─────────────────────────────────────────────────

  function syncFreebieButtons() {
    document.querySelectorAll('.js-freebie-add').forEach(function (b) {
      var btnVariant = String(b.dataset.variant || '');
      b.disabled = !!(selectedFreebieVariantId && btnVariant === String(selectedFreebieVariantId));
    });
  }

  function lockAllFreebieButtons() {
    document.querySelectorAll('.js-freebie-add').forEach(function (b) { b.disabled = true; });
  }

  // ─── Checkout button lock helpers ─────────────────────────────────────────

  function getCheckoutButtons() {
    return document.querySelectorAll(CHECKOUT_SELECTOR);
  }

  function lockCheckoutButton(btn) {
    if (!btn) return;
    btn.disabled = true;
    btn.classList.add('is-checking-freebie');
    var loader = btn.querySelector('#btn-loader') || btn.querySelector('.addloadr');
    if (loader) loader.style.display = '';
  }

  function unlockCheckoutButton(btn) {
    if (!btn) return;
    btn.disabled = false;
    btn.classList.remove('is-checking-freebie');
    var loader = btn.querySelector('#btn-loader') || btn.querySelector('.addloadr');
    if (loader) loader.style.display = 'none';
  }

  /**
   * Single source of truth for checkout lock/disable state.
   * - busy: a freebie add/change or background validation is in flight (transient)
   * - isFreebieIneligible: cart data shows a freebie below threshold (persistent,
   *   independent of whether a request happens to be running right now)
   */
  function updateCheckoutLockState() {
    var busy    = isApplyingFreebie || isValidating;
    var blocked = busy || isFreebieIneligible;

    getCheckoutButtons().forEach(function (btn) {
      if (blocked) lockCheckoutButton(btn); else unlockCheckoutButton(btn);
      btn.classList.toggle('freebie-ineligible', isFreebieIneligible && !busy);
    });
  }

  /**
   * Recomputes persistent eligibility from a fresh cart payload.
   * Called every time we fetch a fresh cart, so it stays correct across
   * tabs, cart:refresh events, and any path that touches the cart.
   */
  function refreshFreebieEligibility(cart) {
    var config     = getConfig();
    var freebieIds = config.freebieIds;
    var threshold  = config.threshold;

    if (!freebieIds.length || !threshold || !cart || !cart.items) {
      isFreebieIneligible = false;
      updateCheckoutLockState();
      return;
    }

    var hasFreebie = getFreebieItems(cart, freebieIds).length > 0;
    isFreebieIneligible = hasFreebie && !isThresholdMet(cart, freebieIds, threshold);

    updateCheckoutLockState();
  }

  // ─── Popup ────────────────────────────────────────────────────────────────

  function openPopup() {
    var p = document.getElementById('freebiePopupNew');
    if (!p) return;
    clearTimeout(popupCloseTimer);
    p.style.display = 'block';
    p.setAttribute('aria-hidden', 'false');
    p.classList.add('is-open');
    fetchFreshCart().then(function (cart) {
      var config  = getConfig();
      var inCart  = getFreebieItems(cart, config.freebieIds);
      selectedFreebieVariantId = inCart.length ? String(inCart[0].variant_id) : null;
      syncFreebieButtons();
    });
  }

  function closePopup() {
    var p = document.getElementById('freebiePopupNew');
    if (!p) return;
    clearTimeout(popupCloseTimer);
    p.classList.remove('is-open');
    p.setAttribute('aria-hidden', 'true');
  }

  function closePopupAfterDelay(delay) {
    clearTimeout(popupCloseTimer);
    popupCloseTimer = setTimeout(function () { closePopup(); }, delay || 2000);
  }

  // ─── Loader ───────────────────────────────────────────────────────────────

  function getCartDrawer() {
    return document.querySelector('cart-drawer') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('#cart-drawer');
  }

  function startCartLoader() {
    var drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.add('is-loading');
    drawer.setAttribute('aria-busy', 'true');
  }

  function stopCartLoader() {
    var drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-loading');
    drawer.removeAttribute('aria-busy');
  }

  function setButtonLoading(btn, mode) {
    if (!btn) return;
    activeButton     = btn;
    activeButtonText = btn.textContent;
    btn.disabled     = true;
    btn.textContent  = mode === 'change' ? 'Changing...' : 'Adding...';
    btn.classList.add('is-loading');
  }

  function resetButtonLoading() {
    if (!activeButton) return;
    activeButton.disabled    = false;
    activeButton.textContent = activeButtonText || activeButton.textContent;
    activeButton.classList.remove('is-loading');
    activeButton     = null;
    activeButtonText = '';
  }

  // ─── Cart fetch helpers ───────────────────────────────────────────────────

  /**
   * Bust cache + fetch fresh cart. Retries up to 3× on 429.
   */
  function fetchFreshCart(retryCount) {
    retryCount = retryCount || 0;
    window.plumBustSharedCart();
    return fetch(CART_JS_URL, { credentials: 'same-origin' })
      .then(function (r) {
        if (r.status === 429) {
          if (retryCount < 3) {
            var delay = (retryCount + 1) * 600;
            return new Promise(function (resolve) {
              setTimeout(function () { resolve(fetchFreshCart(retryCount + 1)); }, delay);
            });
          }
          throw new Error('[freebie] cart.js 429 after 3 retries');
        }
        if (!r.ok) throw new Error('[freebie] cart.js error: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (cart && window.plumSetSharedCart) window.plumSetSharedCart(cart);
        refreshFreebieEligibility(cart);
        return cart;
      });
  }

  // ─── Cart value helpers ───────────────────────────────────────────────────

  function getNonFreebieTotal(cart, freebieIds) {
    return (cart.items || []).reduce(function (sum, item) {
      return freebieIds.includes(Number(item.variant_id))
        ? sum
        : sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function getFreebieItems(cart, freebieIds) {
    return (cart.items || []).filter(function (item) {
      return freebieIds.includes(Number(item.variant_id));
    });
  }

  function isThresholdMet(cart, freebieIds, threshold) {
    if (!freebieIds.length || !threshold) return true;
    return getNonFreebieTotal(cart, freebieIds) >= threshold;
  }

  // ─── Write helpers ────────────────────────────────────────────────────────

  function removeFreebieItemsByKeys(lineKeys, withSection) {
    if (!lineKeys || !lineKeys.length) return Promise.resolve({ removed: false, cart: null });

    var updates = {};
    lineKeys.forEach(function (key) { updates[key] = 0; });
    window.plumBustSharedCart();

    var body = { updates };
    if (withSection) body.sections = SECTION_ID;

    return fetch(CART_UPDATE_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (res) { return { removed: true, cart: res }; });
  }

  function cartAdd(variantId) {
    window.plumBustSharedCart();
    return fetch(CART_ADD_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({
        items:    [{ id: Number(variantId), quantity: 1, properties: { _freebie: 'true' } }],
        sections: SECTION_ID
      })
    }).then(function (r) { if (!r.ok) throw r; return r.json(); });
  }

  function finalizeCartRefresh() {
    return fetchFreshCart().then(function (fullCart) {
      window._plumOwnRefresh = true;
      window.plumRefreshCart(fullCart);
      setTimeout(function () { window._plumOwnRefresh = false; }, 0);
      return fullCart;
    });
  }

  // ─── Click handler ────────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    if (e.target.closest('#freebieTriggerBar')) { openPopup(); return; }
    if (e.target.closest('#freebieClose'))      { closePopup(); return; }

    var popup = document.getElementById('freebiePopupNew');
    if (popup && e.target === popup) { closePopup(); return; }

    var btn = e.target.closest('.js-freebie-add');
    if (!btn || btn.disabled || isApplyingFreebie) return;

    var variantId = btn.dataset.variant;
    if (!variantId) return;

    lockAllFreebieButtons();
    isApplyingFreebie = true;
    updateCheckoutLockState();
    startCartLoader();
    closePopupAfterDelay(2000);

    var config     = getConfig();
    var freebieIds = config.freebieIds;
    var threshold  = config.threshold;

    var op = fetchFreshCart()
      .then(function (cart) {
        var existingItems = getFreebieItems(cart, freebieIds);
        var existingKeys  = existingItems.map(function (i) { return i.key; });
        var eligible      = isThresholdMet(cart, freebieIds, threshold);
        var mode          = existingKeys.length ? 'change' : 'add';

        setButtonLoading(btn, mode);

        if (!eligible) {
          if (!existingKeys.length) return { didWrite: false };
          return removeFreebieItemsByKeys(existingKeys, true)
            .then(function (r) { return { didWrite: r.removed }; });
        }

        if (!existingKeys.length) {
          return cartAdd(variantId).then(function () { return { didWrite: true }; });
        }

        // Change flow: remove → verify clean → add
        return removeFreebieItemsByKeys(existingKeys, false)
          .then(function (removeResult) {
            if (!removeResult.removed) throw new Error('Freebie removal failed');
            return fetchFreshCart();
          })
          .then(function (cartAfterRemove) {
            var stragglers = getFreebieItems(cartAfterRemove, freebieIds);
            if (stragglers.length) {
              return removeFreebieItemsByKeys(stragglers.map(function (i) { return i.key; }), false)
                .then(function () { return cartAdd(variantId); });
            }
            return cartAdd(variantId);
          })
          .then(function () { return { didWrite: true }; });
      })
      .then(function (result) {
        if (result && result.didWrite) {
          selectedFreebieVariantId = String(variantId);
          return finalizeCartRefresh();
        }
        if (result && !result.didWrite) {
          selectedFreebieVariantId = null;
        }
      })
      .catch(function (err) {
        console.error('[freebie] Action failed:', err);
      })
      .finally(function () {
        isApplyingFreebie = false;
        stopCartLoader();
        resetButtonLoading();
        syncFreebieButtons();
        updateCheckoutLockState();
      });

    pendingFreebieOperation = op;
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePopup();
  });

  // ─── Silent background validation ────────────────────────────────────────

  function validateFreebiesSilently() {
    if (isValidating || isApplyingFreebie) return Promise.resolve();

    var config     = getConfig();
    var freebieIds = config.freebieIds;
    var threshold  = config.threshold;

    if (!freebieIds.length || !threshold) return Promise.resolve();

    isValidating = true;
    updateCheckoutLockState();

    var op = fetchFreshCart()
      .then(function (cart) {
        var existing = getFreebieItems(cart, freebieIds);

        if (!cart.items || !cart.items.length) {
          selectedFreebieVariantId = null;
          return { didRemove: false };
        }

        if (isThresholdMet(cart, freebieIds, threshold)) {
          selectedFreebieVariantId = existing.length ? String(existing[0].variant_id) : null;
          return { didRemove: false };
        }

        var keys = existing.map(function (i) { return i.key; });
        if (!keys.length) { selectedFreebieVariantId = null; return { didRemove: false }; }

        return removeFreebieItemsByKeys(keys, true)
          .then(function (r) { return { didRemove: r.removed }; });
      })
      .then(function (result) {
        if (result.didRemove) {
          selectedFreebieVariantId = null;
          return finalizeCartRefresh();
        }
      })
      .catch(function (err) {
        console.error('[freebie:validate] Failed:', err);
      })
      .finally(function () {
        isValidating = false;
        syncFreebieButtons();
        updateCheckoutLockState();
      });

    pendingFreebieOperation = op;
    return op;
  }

  /**
   * 1000ms debounce prevents 429s when multiple cart events fire rapidly.
   */
  function queueValidation() {
    if (isApplyingFreebie) return;
    clearTimeout(validationTimer);
    validationTimer = setTimeout(function () { validateFreebiesSilently(); }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { queueValidation(); });
  } else {
    queueValidation();
  }

  document.addEventListener('cart:refresh', function () {
    if (window._plumOwnRefresh) return;
    queueValidation();
  });

  // ─── Checkout guard ───────────────────────────────────────────────────────
  // Forces a fresh, non-debounced validation the instant checkout is clicked,
  // so we never rely solely on the 1000ms debounce window having already fired.

  function forceValidateAndProceed() {
    return Promise.resolve(pendingFreebieOperation).then(function () {
      clearTimeout(validationTimer);
      return validateFreebiesSilently();
    });
  }

  // Capture phase: runs before the checkout button's own inline onclick.
  document.addEventListener('click', function (e) {
    var checkoutBtn = e.target.closest(CHECKOUT_SELECTOR);
    if (!checkoutBtn || checkoutBtn.dataset.freebieBypass === 'true') return;

    e.preventDefault();
    e.stopPropagation(); // blocks the button's own inline onclick from firing this pass

    lockCheckoutButton(checkoutBtn);

    var guard   = forceValidateAndProceed();
    var timeout = new Promise(function (resolve) { setTimeout(resolve, CHECKOUT_GUARD_TIMEOUT_MS); });

    Promise.race([guard, timeout])
      .catch(function (err) {
        console.error('[freebie:checkout-guard] validation failed, proceeding anyway:', err);
      })
      .finally(function () {
        updateCheckoutLockState(); // re-applies lock if still ineligible, unlocks otherwise
        checkoutBtn.dataset.freebieBypass = 'true';
        if (typeof window.onCheckoutClick === 'function') {
          window.onCheckoutClick(checkoutBtn);
        } else {
          checkoutBtn.click(); // fallback, safe due to bypass flag
        }
        setTimeout(function () { checkoutBtn.dataset.freebieBypass = ''; }, 0);
      });
  }, true); // capture: true is essential — must run before the target's own onclick
})();